import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { useAuth } from '../auth.context';
import { StyledInput } from './StyledInput';

export default function ResetPasswordForm() {
  const { resetPassword, loading, user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Reached both ways: from the login page (nobody signed in, username typed) and from
  // the sidebar while signed in, where the account is already known.
  const methods = useForm<any>({
    defaultValues: {
      username: (user?.username as string) ?? '',
      // Field name matches the Controller below. It used to read `currentPassword` while
      // the input registered as `password`, so the old password submitted was always ''.
      password: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = methods;

  const watchNewPassword = watch('newPassword');

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
  const toggleNewPasswordVisibility = () => setShowNewPassword((prev) => !prev);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword((prev) => !prev);

  const onSubmit = async (data) => {
    try {
      // `ResetPasswordDto` binds camelCase — Jackson has no snake_case naming strategy
      // configured. Sending `old_password` / `password_confirmation` made the API reject
      // the whole body with "Unrecognized field", so this form never once succeeded.
      const payload = {
        username: data.username,
        oldPassword: data.password,
        password: data.newPassword,
        passwordConfirmation: data.confirmPassword,
      };
      const { success, error } = await resetPassword(payload);
      if (!success) throw new Error(error);

      toast.success('Password changed. Please sign in again.');
      // The old credential is gone, so any existing session is stale — drop it and make
      // the user re-authenticate with the new password.
      if (isAuthenticated) await logout();
      navigate('/login');
    } catch (err) {
      toast.error(
        logError({ message: 'Password reset failed', error: err, code: 'AUTH_ACTION_FAILED' })
      );
    }
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component='form'
        sx={{ width: '100%', maxWidth: '400px', gap: 10 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Typography
          variant='h1'
          fontSize='2rem'
          fontWeight={600}
          textAlign='center'
          color='primary.contrastText'
        >
          {isAuthenticated ? 'Change Password' : 'Reset Password'}
        </Typography>

        <Stack gap={5}>
          <Stack gap={1}>
            <Typography
              component='label'
              htmlFor='username'
              fontSize='1.25rem'
              color='primary.contrastText'
            >
              Username
            </Typography>
            <Controller
              name='username'
              control={control}
              rules={{ required: 'Username is required.' }}
              render={({ field }) => (
                <StyledInput {...field} id='username' type='text' autoComplete='username' />
              )}
            />
            {errors.username && (
              <Typography color='error' fontSize='0.75rem'>
                {String(errors.username?.message || "")}
              </Typography>
            )}
          </Stack>

          <Stack gap={1}>
            <Typography
              component='label'
              htmlFor='password'
              fontSize='1.25rem'
              color='primary.contrastText'
            >
              Current Password
            </Typography>
            <Box position='relative' width='100%'>
              <Controller
                name='password'
                control={control}
                rules={{
                  // Only required: an existing password shorter than today's rule must still work.
                  required: 'Current Password is required.',
                }}
                render={({ field }) => (
                  <StyledInput
                    {...field}
                    id='password'
                    type={showPassword ? 'text' : 'password'}
                    autoComplete='current-password'
                  />
                )}
              />
              <IconButton
                type='button'
                onClick={togglePasswordVisibility}
                aria-label='Toggle password visibility'
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'primary.contrastText',
                }}
              >
                <Icon icon={showPassword ? 'mdi:eye-off' : 'mdi:eye'} />
              </IconButton>
            </Box>
            {errors.password && (
              <Typography color='error' fontSize='0.75rem'>
                {String(errors.password?.message || "")}
              </Typography>
            )}
          </Stack>

          <Stack gap={1}>
            <Typography
              component='label'
              htmlFor='new-password'
              fontSize='1.25rem'
              color='primary.contrastText'
            >
              New Password
            </Typography>
            <Box position='relative' width='100%'>
              <Controller
                name='newPassword'
                control={control}
                rules={{
                  required: 'New Password is required.',
                  minLength: { value: 6, message: 'Password must be at least 6 characters.' },
                }}
                render={({ field }) => (
                  <StyledInput
                    {...field}
                    id='new-password'
                    type={showNewPassword ? 'text' : 'password'}
                    autoComplete='new-password'
                  />
                )}
              />
              <IconButton
                type='button'
                onClick={toggleNewPasswordVisibility}
                aria-label='Toggle password visibility'
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'primary.contrastText',
                }}
              >
                <Icon icon={showNewPassword ? 'mdi:eye-off' : 'mdi:eye'} />
              </IconButton>
            </Box>
            {errors.newPassword && (
              <Typography color='error' fontSize='0.75rem'>
                {String(errors.newPassword?.message || "")}
              </Typography>
            )}
          </Stack>

          <Stack gap={1}>
            <Typography
              component='label'
              htmlFor='confirm-password'
              fontSize='1.25rem'
              color='primary.contrastText'
            >
              Confirm Password
            </Typography>
            <Box position='relative' width='100%'>
              <Controller
                name='confirmPassword'
                control={control}
                rules={{
                  required: 'Password confirmation is required.',
                  minLength: { value: 6, message: 'Password must be at least 6 characters.' },
                  validate: (value) => value === watchNewPassword || 'Passwords do not match.',
                }}
                render={({ field }) => (
                  <StyledInput
                    {...field}
                    id='confirm-password'
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete='confirm-password'
                  />
                )}
              />
              <IconButton
                type='button'
                onClick={toggleConfirmPasswordVisibility}
                aria-label='Toggle password visibility'
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'primary.contrastText',
                }}
              >
                <Icon icon={showConfirmPassword ? 'mdi:eye-off' : 'mdi:eye'} />
              </IconButton>
            </Box>
            {errors.confirmPassword && (
              <Typography color='error' fontSize='0.75rem'>
                {String(errors.confirmPassword?.message || "")}
              </Typography>
            )}
          </Stack>

          <Button
            type='submit'
            variant='contained'
            loading={loading}
            sx={{
              width: '100%',
              padding: '12px 32px',
              fontSize: '1.25rem',
              borderRadius: 2,
              backgroundColor: 'primary.contrastText',
              color: 'primary.main',
              ':disabled': {
                backgroundColor: 'primary.contrastText',
                color: 'primary.main',
                opacity: 0.5,
              },
            }}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
          <MuiLink
            component={Link}
            to={isAuthenticated ? '/' : '/login'}
            sx={{ width: 'fit-content', marginTop: -3, alignSelf: 'center' }}
          >
            {isAuthenticated ? 'Back to Dashboard' : 'Back to Login'}
          </MuiLink>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
